import {
  CloseModal,
  GetAccessToken,
  GetLanguage,
  GetPageUrl,
  Host,
  Navigate,
  ObserveState,
  OnBeforeUnload,
  OpenModal,
  ShowToast,
  RequestFeedback,
  SetPageTitle,
  NavigateBack,
  OnLayerStateChange,
  ProviderOpenModal,
  OpenMediaManager,
} from './types';
import {
  AddSitePlugin,
  AddSitePluginModalError,
  AddSitePluginModalResult,
} from './types/addSitePlugin';
import { InstallApp } from './types/installApp';
import { GetDashboardMenuPlugins, GetDashboardPlugins } from './types/slots';
import { GetWidget, GetWidgetAsync } from './types/widget';
import { GetSiteInfo } from './types/getSiteInfo';
import type { CreateStore, GetStore } from './types/store';

export const createSDKBuilders = () => {
  const showToast =
    ({ channel }: Host): ShowToast =>
    (...args) => {
      const handle = channel.showToast(...args);
      const remove = () => Promise.resolve(handle).then((h) => h.remove());
      return { remove };
    };

  const observeState =
    ({ channel }: Host): ObserveState =>
    (...args) => {
      const handle = (channel.observeState as ObserveState)(
        ...args,
      ) as ReturnType<Host['channel']['observeState']>; // this temporary casting is needed because it seems that typescript can't handle this by itself.
      const disconnect = () =>
        Promise.resolve(handle).then((h) => h.disconnect());
      return { disconnect };
    };

  const navigate =
    ({ channel }: Host): Navigate =>
    (...args) => {
      return channel.navigate(...args);
    };

  const navigateBack =
    ({ channel }: Host): NavigateBack =>
    () => {
      return channel.navigateBack();
    };

  const openModal =
    ({ channel }: Host): OpenModal =>
    (modalInfo, modalParams?) => {
      const [modalId, params]: Parameters<ProviderOpenModal> =
        typeof modalInfo === 'string'
          ? [modalInfo, modalParams]
          : [modalInfo.modalId, modalInfo.params];

      const handle = channel.openModal(modalId, params);
      return {
        modalClosed: Promise.resolve(handle).then((h) => h.modalClosed),
      };
    };

  const closeModal =
    ({ channel }: Host): CloseModal =>
    (closeData) => {
      if (closeData === undefined) {
        return channel.closeModal();
      }
      const filteredArg = JSON.parse(JSON.stringify(closeData));
      return channel.closeModal(filteredArg);
    };

  const getAccessToken =
    ({ channel }: Host): GetAccessToken =>
    () => {
      return channel.getAccessToken();
    };

  const getPageUrl =
    ({ channel }: Host): GetPageUrl =>
    (...args) => {
      return channel.getPageUrl(...args);
    };

  const openMediaManager =
    ({ channel }: Host): OpenMediaManager =>
    (...args) => {
      return channel.openMediaManager(...args);
    };

  const addSitePlugin =
    ({ channel }: Host): AddSitePlugin =>
    (pluginId, options) => {
      const PLUGINS_DASHBOARD_MODAL_ID = '802c76e4-b381-4bb3-9152-96acabbf3194';
      const additionalParams = options?.placement || {};

      // The modal reports a failure here before it closes. If the user then
      // dismisses the modal implicitly (overlay click / Esc), the host resolves
      // modalClosed with no result and we'd otherwise default to
      // ABORTED_BY_USER (3006), masking the real failure. Cache the last
      // reported error and surface it instead. The modal clears it (calls
      // onError with no argument) when the user retries rather than aborts.
      // See https://wix.atlassian.net/browse/EP-8591.
      let bmSdkError: { message: string; code: number } | undefined;
      const onError = (error?: { message: string; code: number }) => {
        bmSdkError = error;
      };

      const handle = channel.openModal(PLUGINS_DASHBOARD_MODAL_ID, {
        ...additionalParams,
        placementsSlotId: options?.slotId,
        pluginId,
        action: 'populate',
        onError,
      });
      return Promise.resolve(handle)
        .then(
          (h) => h.modalClosed as unknown as Promise<AddSitePluginModalResult>,
        )
        .then((resp) => {
          if (resp?.type === 'ERROR') {
            throw new AddSitePluginModalError(resp.data.error);
          }
          if (!resp?.type) {
            throw new AddSitePluginModalError(
              bmSdkError ?? { message: 'Aborted by user', code: 3006 },
            );
          }
        });
    };

  const onBeforeUnload =
    ({ channel }: Host): OnBeforeUnload =>
    (...args) => {
      return channel.onBeforeUnload(...args);
    };

  const setPageTitle =
    ({ channel }: Host): SetPageTitle =>
    (pageTitle: string | null) => {
      return channel.setPageTitle(pageTitle);
    };

  const getLanguage = (host: Host): GetLanguage => {
    let language: string | null = null;
    return async () => {
      if (language) {
        return language;
      }
      let observeStateResult = null as ReturnType<ObserveState> | null;
      language = await new Promise<string>((resolve) => {
        observeStateResult = observeState(host)((_, envState) => {
          resolve(envState.language);
        });
      });
      observeStateResult?.disconnect();
      return language;
    };
  };

  const requestFeedback =
    ({ channel }: Host): RequestFeedback =>
    (...args) => {
      return channel.requestFeedback(...args);
    };

  const onLayerStateChange =
    ({ channel }: Host): OnLayerStateChange =>
    (...args) => {
      const resultOrPromise = channel.onLayerStateChange(...args);
      const remove = () =>
        Promise.resolve(resultOrPromise).then((h) => h.remove());
      return { remove };
    };

  const installApp = ({ channel }: Host): InstallApp => channel.installApp;

  const getWidget =
    ({ channel }: Host): GetWidget =>
    (...args) => {
      if ('getWidget' in channel) {
        return channel.getWidget(...args);
      } else {
        throw new Error('Unsupported');
      }
    };

  const getWidgetAsync =
    ({ channel }: Host): GetWidgetAsync =>
    <P extends {}>(widgetId: string) => {
      if ('getWidgetAsync' in channel) {
        return channel.getWidgetAsync<P>(widgetId);
      } else {
        throw new Error('Unsupported');
      }
    };

  const getDashboardPlugins =
    ({ channel }: Host): GetDashboardPlugins =>
    (...args) => {
      if ('getDashboardPlugins' in channel) {
        return channel.getDashboardPlugins(...args);
      } else {
        throw new Error('Unsupported');
      }
    };

  const getDashboardMenuPlugins =
    ({ channel }: Host): GetDashboardMenuPlugins =>
    (...args) => {
      if ('getDashboardMenuPlugins' in channel) {
        return channel.getDashboardMenuPlugins(...args);
      } else {
        throw new Error('Unsupported');
      }
    };

  const getSiteInfo = ({ channel }: Host): GetSiteInfo => channel.getSiteInfo;

  const createStore = ({ channel }: Host): CreateStore | undefined => {
    if (channel.createStore) {
      return channel.createStore as CreateStore;
    }
    return undefined;
  };

  const getStore = ({ channel }: Host): GetStore | undefined => {
    if (channel.getStore) {
      return channel.getStore as GetStore;
    }
    return undefined;
  };

  return {
    showToast,
    observeState,
    navigate,
    navigateBack,
    openModal,
    closeModal,
    getAccessToken,
    getPageUrl,
    openMediaManager,
    addSitePlugin,
    onBeforeUnload,
    setPageTitle,
    getLanguage,
    requestFeedback,
    onLayerStateChange,
    installApp,
    getSiteInfo,
    getWidget,
    getWidgetAsync,
    getDashboardPlugins,
    getDashboardMenuPlugins,
    createStore,
    getStore,
  };
};
